/* @flow */
import React, { Component, Fragment } from 'react';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';

import CardPayment from './CardPayment';
import PaymentButtons from './PaymentButtons';
import PaymentInfo from './PaymentInfo';
import PaymentVoucher from './PaymentVoucher';
import PaymentHeader from './PaymentHeader';
import i18n from '../../../../i18n/i18n.js';

type Props = {
    onSubmit: Function,
    handleBack: Function,
    getDiscount: Function,
    discount: {
        amount: number,
        message: string,
    },
    selectedFee: {
        id: number,
        amount: number,
        currency: string,
        access_type: {
            period: string,
        },
        setup_fee: {
            amount: number,
            description: string,
            fee_amount: number,
        },
        trial_period: {
            quantity: number,
            period: string,
        },
    },
    branding: {
        paywall_primary_color: String,
    },
    lng: string,
    title: string,
    clientID: string,
    payment: {
        paymentMethods: Object,
    },
    params: Object,
};

type State = {
    errorMessage: string,
    discountAmount: string,
    paymentMethod: string,
    isInternal: boolean,
    activeCard: boolean,
    activePaypal: boolean,
    name: string,
    active: boolean,
    hasInputValue: boolean,
    hasCardEnabled: boolean,
    hasPaypalEnabled: boolean,
    voucherValue: string,
};

export default class PaymentForm extends Component<Props, State> {
    constructor(props) {
        super(props);
        this.state = {
            errorMessage: '',
            discountAmount: '',
            paymentMethod: '',
            activeCard: true,
            activePaypal: false,
            name: '',
            active: false,
            hasInputValue: false,
            hasCardEnabled: false,
            hasPaypalEnabled: false,
            voucherValue: '',
            addInputChecked: false,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.payment.paymentMethods) {
            const hasCardEnabled =
                find(nextProps.payment.paymentMethods, method => {
                    return method.method_name === 'Card';
                }) !== undefined;
            const hasPaypalEnabled =
                find(nextProps.payment.paymentMethods, method => {
                    return method.method_name === 'PayPal';
                }) !== undefined;

            let paymentMethod = this.state.paymentMethod;
            if (this.state.paymentMethod === '') {
                paymentMethod =
                    hasCardEnabled && hasPaypalEnabled
                        ? 'creditcard'
                        : !hasCardEnabled && hasPaypalEnabled
                            ? 'paypal'
                            : 'creditcard';
            }

            this.setState({
                hasCardEnabled,
                hasPaypalEnabled,
                paymentMethod,
            });
        }

        this.setState({
            errorMessage: nextProps.discount.message,
            discountAmount: nextProps.discount.amount,
        });
    }

    deleteVoucher = () => {
        this.props.resetVoucherValue();
        this.setState({
            voucherValue: '',
        });
    };

    changePaymentMethod = e => {
        this.setState(
            {
                paymentMethod: e.target.value,
                activeCard: e.target.value === 'creditcard',
                activePaypal: e.target.value === 'paypal',
                addInputChecked: false,
            },
            this.deleteVoucher
        );
    };

    checkVoucherCode = (e: Object) => {
        e.persist();
        if (e.target === null) return;

        this.setState(
            {
                voucherValue: e.target.value,
                hasInputValue: e.target.value !== '',
            },
            function() {
                this.props.getDiscount(
                    e.target.value,
                    this.props.selectedFee.id,
                    this.props.clientID
                );
            }
        );
    };

    handleFocus = e => {
        e.preventDefault();
        this.setState({
            name: e.target.name,
            active: !this.state.active,
        });
    };

    toggleCheckbox = () => {
        this.setState({ addInputChecked: !this.state.addInputChecked });
    };

    render() {
        const {
            onSubmit,
            title,
            selectedFee,
            branding,
            params,
            lng,
            payment,
        } = this.props;

        const { hasCardEnabled, hasPaypalEnabled } = this.state;

        const headerTitle = hasCardEnabled ? 'pay' : 'pay_with';
        // Validation error message
        const voucherErrorMessage =
            this.state.errorMessage !== '' ? 'Invalid voucher code.' : '';

        // Selected Fee is subscription with setup fee
        const subSetupFee =
            !isEmpty(selectedFee) &&
            selectedFee.access_type.name === 'subscription' &&
            selectedFee.setup_fee;
        // Selected Fee is subscription with free-trial period and 100% voucher
        const subFreeFullVoucher =
            !isEmpty(selectedFee) &&
            selectedFee.access_type.name === 'subscription' &&
            selectedFee.trial_period &&
            this.state.discountAmount === 0;
        // Selected Fee is ppv with 100% voucher
        const ppvfullVoucher =
            !isEmpty(selectedFee) &&
            selectedFee.access_type.name === 'ppv' &&
            this.state.discountAmount === 0;
        // Check if paypall option is enabled
        const disabled = subSetupFee || subFreeFullVoucher || ppvfullVoucher;
        // Check if voucher is valid
        const invalidVoucher = this.state.errorMessage !== '';

        // Branding
        let borderCard;
        let borderPaypal;
        let backgroundCard;
        let backgroundPaypal;
        let iconColor;
        if (this.state.activeCard) {
            borderCard =
                '1px solid ' +
                (branding.paywall_primary_color
                    ? branding.paywall_primary_color
                    : '#00aae6');
            backgroundCard = '#fff';
            iconColor = branding.paywall_primary_color
                ? branding.paywall_primary_color
                : '#00aae6';
        } else if (this.state.activePaypal) {
            borderPaypal =
                '1px solid ' +
                (branding.paywall_primary_color
                    ? branding.paywall_primary_color
                    : '#00aae6');
            backgroundPaypal = '#fff';
        }

        // Branding - button
        let backgroundButtonColor = branding.paywall_buttons_bg_color
            ? branding.paywall_buttons_bg_color
            : '#00aae6';
        let textButtonColor = branding.paywall_buttons_text_color
            ? branding.paywall_buttons_text_color
            : '#fff';

        return (
            <Fragment>
                {hasCardEnabled && hasPaypalEnabled ? (
                    <PaymentHeader
                        lng={lng}
                        borderCard={borderCard}
                        backgroundCard={backgroundCard}
                        iconColor={iconColor}
                        borderPaypal={borderPaypal}
                        backgroundPaypal={backgroundPaypal}
                        changePaymentMethod={this.changePaymentMethod}
                    />
                ) : null}
                <section id="ipx-maincontent">
                    {payment.isPaymentLoading ? (
                        <svg
                            className="spinner"
                            viewBox="0 0 66 66"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{
                                left: '44%',
                                height: '57px',
                                position: 'relative',
                            }}
                        >
                            <circle
                                className="path"
                                fill="none"
                                strokeWidth="6"
                                strokeLinecap="round"
                                cx="33"
                                cy="33"
                                r="30"
                                stroke={iconColor}
                            />
                        </svg>
                    ) : hasCardEnabled || hasPaypalEnabled ? (
                        <span>
                            {!hasCardEnabled || !hasPaypalEnabled ? (
                                <div
                                    className="headline"
                                    style={{ marginBottom: '30px' }}
                                >
                                    <h4>{i18n.t(headerTitle, { lng })}</h4>
                                    {hasPaypalEnabled ? (
                                        <div className="payption header">
                                            <i className="paypal" />
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}

                            <form onSubmit={onSubmit}>
                                {/* To enable to check which payment method in selected.
                                            Used in Payments container. */}
                                <input
                                    className="paymentmethod"
                                    id="selectedMethod"
                                    name="selectedMethod"
                                    value={this.state.paymentMethod}
                                    readOnly
                                />
                                <div className="formelements vouchie">
                                    <PaymentInfo
                                        title={title}
                                        selectedFee={selectedFee}
                                        lng={lng}
                                        paymentMethod={this.state.paymentMethod}
                                        subSetupFee={subSetupFee}
                                        subFreeFullVoucher={subFreeFullVoucher}
                                        ppvfullVoucher={ppvfullVoucher}
                                    />
                                    <PaymentVoucher
                                        lng={lng}
                                        discountAmount={
                                            this.state.discountAmount
                                        }
                                        errorMessage={voucherErrorMessage}
                                        deleteVoucher={this.deleteVoucher}
                                        checkVoucherCode={this.checkVoucherCode}
                                        branding={
                                            branding.paywall_primary_color
                                        }
                                        handleFocus={this.handleFocus}
                                        active={this.state.active}
                                        hasInputValue={this.state.hasInputValue}
                                        voucherValue={this.state.voucherValue}
                                        addInputChecked={
                                            this.state.addInputChecked
                                        }
                                        toggleCheckbox={this.toggleCheckbox}
                                        setupFee={selectedFee.setup_fee}
                                    />
                                </div>
                                {this.state.paymentMethod !== 'paypal' &&
                                ((!isEmpty(selectedFee) &&
                                    selectedFee.access_type.name ===
                                        'subscription') ||
                                    this.state.discountAmount === undefined ||
                                    this.state.discountAmount === '' ||
                                    this.state.discountAmount !== 0) ? (
                                    <CardPayment
                                        branding={branding}
                                        lng={lng}
                                        name={this.state.name}
                                        active={this.state.active}
                                        handleFocus={this.handleFocus}
                                        dispatch={this.props.dispatch}
                                    />
                                ) : null}
                                <PaymentButtons
                                    paymentMethod={this.state.paymentMethod}
                                    branding={branding}
                                    lng={lng}
                                    backgroundButtonColor={
                                        backgroundButtonColor
                                    }
                                    textButtonColor={textButtonColor}
                                    params={params}
                                    disabled={disabled}
                                    discountAmount={this.state.discountAmount}
                                    handleBack={this.props.handleBack}
                                    invalidVoucher={invalidVoucher}
                                />
                            </form>
                        </span>
                    ) : (
                        <p>
                            No payment methods are enabled. Please contact out
                            suppor team.
                        </p>
                    )}
                </section>
            </Fragment>
        );
    }
}



// WEBPACK FOOTER //
// ./src/ui/components/Forms/Payments/index.js